import React from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { JSONSchema7 } from 'json-schema';

import BusinessNameInputProps from '../../atoms/BusinessNameInput/BusinessNameInput.types';
import ButtonGroupProps from '../../atoms/ButtonGroup/ButtonGroup.types';
import CheckboxProps from '../../atoms/Checkbox/Checkbox.types';
import InputProps from '../../atoms/Input/Input.types';
import HiddenInputProps from '../../atoms/HiddenInput/HiddenInput.types';
import MaskedInputProps from '../../atoms/MaskedInput/MaskedInput.types';
import RadioButtonsProps from '../../atoms/RadioButtons/RadioButtons.types';
import PhoneInputProps from '../../atoms/PhoneInput/PhoneInput.types';
import { SelectProps } from '../../atoms/Select/Select.types';
import DateInputProps from '../../molecules/DateInput/DateInput.types';
import { IncrementalInputProps } from '../../molecules/IncrementalInput/IncrementalInput.types';
import { InputComponentNames } from '../../molecules/InputField/InputField.types';
import TypeaheadProps from '../../molecules/Typeahead/Typeahead.types';
import { VerificationInputProps } from '../../molecules/VerificationInput/VerificationInput.types';
import AddressLookupProps from '../AddressLookup/AddressLookup.types';
import BusinessDetailsProps from '../BusinessDetails/BusinessDetails.types';
import LoqateAddressLookupProps, { MyAddressItems } from '../LoqateAddressLookup/LoqateAddressLookup.types';
import OwnershipStructureProps from '../OwnershipStructure/OwnershipStructure.types';

import TextProps from '../../atoms/Text/Text.types';
import ParsedTextProps from '../../atoms/ParsedText/ParsedText.types';
import ConfirmationProps, { ConfirmationSection } from '../../molecules/ConfirmationCard/ConfirmationCard.types';
import ButtonProps from '../../atoms/Button/Button.types';
import ListProps from '../../atoms/List/List.types';
import AcknowledgeProps from '../../molecules/Acknowledge/Acknowledge.types';
import { ResetFormButtonProps } from '../../molecules/ResetFormButton/ResetFormButton.types';
import { ContractIFrameProps } from '../../molecules/ContractIFrame/ContractIFrame.types';
import Indexable from '../../shared/types/Indexable.types';
import Optional from '../../shared/types/Optional.types';
import RecursivelyNested from '../../shared/types/RecursivelyNested.types';
import Replace from '../../shared/types/Replace.types';

interface errorMessage {
  required?: string;
  minLength?: string;
  enum?: string;
  displayOnInit?: string;
  pattern?: string;
  format?: string;
  minProperties?: string;
}

type ComponentProps =
  | BusinessNameInputProps
  | ButtonGroupProps
  | CheckboxProps
  | InputProps
  | HiddenInputProps
  | MaskedInputProps
  | RadioButtonsProps
  | PhoneInputProps
  | SelectProps
  | DateInputProps
  | IncrementalInputProps
  | TypeaheadProps
  | VerificationInputProps
  | AddressLookupProps
  | BusinessDetailsProps
  | LoqateAddressLookupProps
  | OwnershipStructureProps;

interface EditableOptional {
  confirmationSections?: { [key: string]: Optional<ConfirmationSection, 'editable'> };
}
type ConfirmationPropsEditableOptional = Optional<
  Replace<ConfirmationProps, 'confirmationSections', EditableOptional>,
  'data'
>;

type AdditionalComponentProps =
  | TextProps
  | ParsedTextProps
  | ConfirmationPropsEditableOptional
  | ButtonProps
  | ListProps
  | AcknowledgeProps
  | ResetFormButtonProps
  | ContractIFrameProps;

const AdditionalComponentNames = {
  Text: 'Text',
  ParsedText: 'ParsedText',
  ConfirmationCard: 'ConfirmationCard',
  Button: 'Button',
  List: 'List',
  Acknowledge: 'Acknowledge',
  ResetFormButton: 'ResetFormButton',
  ContractIFrame: 'ContractIFrame',
} as const;

type AdditionalComponentNamesType = typeof AdditionalComponentNames[keyof typeof AdditionalComponentNames];

interface ToolTip {
  toolTip: {
    tip: string;
  };
}

interface InputFieldProps extends ToolTip {
  hideError?: boolean;
  align?: string;
  noMargin?: boolean;
  hiddenLabel?: boolean;
}

type InputFieldNamesToComponents =
  | {
      component: 'addressLookup';
      properties: Partial<AddressLookupProps & InputFieldProps>;
    }
  | {
      component: 'businessDetails';
      properties: Partial<BusinessDetailsProps & InputFieldProps>;
    }
  | {
      component: 'businessNameInput';
      properties: Partial<BusinessNameInputProps & InputFieldProps>;
    }
  | {
      component: 'buttonGroup';
      properties: Partial<ButtonGroupProps & InputFieldProps>;
    }
  | {
      component: 'checkbox';
      properties: Partial<CheckboxProps & InputFieldProps>;
    }
  | {
      component: 'dateInput';
      properties: Partial<DateInputProps & InputFieldProps>;
    }
  | {
      component: 'hiddenInput';
      properties: Partial<HiddenInputProps & InputFieldProps>;
    }
  | {
      component: 'incrementalInput';
      properties: Partial<IncrementalInputProps & InputFieldProps>;
    }
  | ({
      component: 'loqateAddressLookup';
      properties: Partial<LoqateAddressLookupProps & InputFieldProps>;
    } & MyAddressItems)
  | {
      component: 'maskedInput';
      properties: Partial<MaskedInputProps & InputFieldProps>;
    }
  | {
      component: 'number';
      properties: Partial<InputProps & InputFieldProps>;
    }
  | {
      component: 'ownershipStructure';
      properties: Partial<OwnershipStructureProps & InputFieldProps>;
    }
  | {
      component: 'phoneInput';
      properties: Partial<PhoneInputProps & InputFieldProps>;
    }
  | {
      component: 'radioButtons';
      properties: Partial<RadioButtonsProps & InputFieldProps>;
    }
  | {
      component: 'select';
      properties: Partial<SelectProps & InputFieldProps>;
    }
  | {
      component: 'text';
      properties: Partial<InputProps & InputFieldProps>;
    }
  | {
      component: 'typeahead';
      properties: Partial<TypeaheadProps & InputFieldProps>;
    }
  | {
      component: 'verificationInput';
      properties: Partial<VerificationInputProps & InputFieldProps>;
    };

interface StepProperty {
  header?: StepPropertyText;
  description?: StepPropertyText;
  prependAdditionalComponents?: AdditionalComponent[];
  appendAdditionalComponents?: AdditionalComponent[];
  nextButton?: {
    component?: 'Button';
    properties?: {
      text: string;
    };
    hidden?: boolean;
  };
  submitButton?: { hidden: boolean; layout?: string };
  onBackStep?: { action: string };
  stepArrows?: { resetFormData: boolean };
  align?: string;
  validationSchema?: JSONSchema7;
}

interface StepPropertyText {
  component?: string;
  properties: {
    children: string;
  };
}

export interface AdditionalComponent {
  component: InputComponentNames | AdditionalComponentNamesType;
  internalProperties?: {
    useInternalValues?: string[];
    layout?: string;
    onClick?: {
      action: string;
      params: (Indexable<number | string> | number | boolean | Indexable<string[]>)[];
      clearForwardData?: boolean;
    };
  };
  properties?: ComponentProps | AdditionalComponentProps | { isEditable: boolean };
}

interface InternalProperties {
  useInternalValues?: string[];
  onload?: { hideChildren?: boolean; hideComponent?: boolean };
}

interface Condition {
  fact: string;
  operator: string;
  value: string;
}

interface Conditions {
  [key: string]: Condition[];
}

type Step = Partial<InputFieldNamesToComponents> & {
  step?: number;
  errorMessage?: errorMessage;
  internalProperties?: InternalProperties;
  placeholder?: string;
  align?: string;
  modifierIndex?: number;
  triggers?: { name: string; dataPointer?: string; triggerValue?: any; from?: string; to?: string; value?: any }[];
  condition?: Conditions;
};

interface FirstLevelProperties {
  component?: string;
  step?: number[];
  loader?: {
    showAfterSubmit: boolean;
    text: ['One moment while we generate your decision.', 'One more moment while we generate your quote!'];
    interval?: number;
  };
  properties?: {
    color?: string;
    text?: string;
    hidden?: boolean;
    'data-edit-text'?: string;
    align?: string;
    children?: string;
    heading?: 'h1' | 'h2' | 'h3';
  };
}

export interface UiSchemaProps {
  [key: string]: (RecursivelyNested<StepProperty | Step> | Step | FirstLevelProperties) &
    errorMessage & {
      additionalStepIndex?: number[];
      internalProperties?: InternalProperties;
      stepArrows?: { alwaysVisible?: boolean };
    };
  submitButton?: FirstLevelProperties;
  header?: FirstLevelProperties;
}

export interface dataSchemaProps extends JSONSchema7 {}

export interface indexArrayLookupType {
  [key: string]: number;
}

export enum ChangeStepDirection {
  FORWARD = 1,
  BACKWARD = -1,
}

export default interface FormProps extends React.FormHTMLAttributes<HTMLFormElement> {
  dataSchema: JSONSchema7;
  uiSchema: UiSchemaProps;
  serviceCaller?: (args) => void;
  halfContainer?: boolean;
  onStepChange?: (activeStep: number) => void;
  dataCyAttributes?: {
    form?: string;
    stepArrows?: {
      forward?: string;
      backward?: string;
    };
  };
}
